import React, { useState, useEffect, useCallback } from 'react';
import { medicalTourismAPI, usersAPI } from '../services/api';
import { useOpenParam } from '../utils/openParam';
import { useAuth } from '../contexts/AuthContext';
import {
  FiRefreshCw, FiAlertCircle, FiPlus, FiX, FiEdit2, FiSearch,
  FiLock, FiUnlock, FiKey, FiCopy, FiUpload, FiFile, FiClock,
} from 'react-icons/fi';
import './Students.css';
import ClientJourneyPanel from './ClientJourneyPanel';
import ClientStepsPanel from './ClientStepsPanel';
import LessonsPanel from './LessonsPanel';
import './StudentModal.css';
import './ClientVertical.css';

const STATUSES = [
  ['NEW', 'New', '#011ff3'],
  ['CONTACTED', 'Contacted', '#f093fb'],
  ['CONSULTATION', 'Consultation Scheduled', '#4facfe'],
  ['TREATMENT_PLANNED', 'Treatment Planned & Quoted', '#f5a623'],
  ['DOCS_COLLECTED', 'Documents Collected', '#f5a623'],
  ['VISA_TRAVEL', 'Visa & Travel Arranged', '#4facfe'],
  ['IN_TREATMENT', 'In Treatment', '#8b5cf6'],
  ['RECOVERY', 'Recovery / Follow-up', '#43e97b'],
  ['COMPLETED', 'Completed', '#38f9d7'],
  ['CANCELLED', 'Cancelled', '#f5576c'],
];

const PROCEDURE_TYPES = [
  ['DENTAL', 'Dental'],
  ['COSMETIC', 'Cosmetic / Plastic Surgery'],
  ['ORTHOPEDIC', 'Orthopedic'],
  ['CARDIAC', 'Cardiac'],
  ['FERTILITY', 'Fertility / IVF'],
  ['OPHTHALMOLOGY', 'Ophthalmology (Eye)'],
  ['ONCOLOGY', 'Oncology'],
  ['BARIATRIC', 'Bariatric / Weight-loss Surgery'],
  ['HAIR_TRANSPLANT', 'Hair Transplant'],
  ['OTHER', 'Other'],
];

const PAYMENT_STATUSES = [
  ['NOT_PAID', 'Not Paid', '#f5576c'],
  ['PARTIAL', 'Partial', '#f5a623'],
  ['PAID', 'Paid', '#43e97b'],
  ['REFUNDED', 'Refunded', '#999'],
];

const GENDER_CHOICES = ['Male', 'Female', 'Other', 'Prefer not to say'];

const DOCUMENT_TYPES = [
  ['PASSPORT', 'Passport'],
  ['MEDICAL_RECORD', 'Medical Record'],
  ['REFERRAL_LETTER', 'Referral Letter'],
  ['LAB_RESULTS', 'Lab Results'],
  ['SCAN_IMAGING', 'Scan / Imaging'],
  ['OTHER', 'Other'],
];

const statusColor = (s) => (STATUSES.find((x) => x[0] === s) || [null, null, '#999'])[2];
const statusLabel = (s) => (STATUSES.find((x) => x[0] === s) || [null, s])[1];
const paymentColor = (s) => (PAYMENT_STATUSES.find((x) => x[0] === s) || [null, null, '#999'])[2];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// ── Documents sub-section (only shown when editing an existing client) ──
const ClientDocuments = ({ clientId }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [docType, setDocType] = useState('PASSPORT');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await medicalTourismAPI.documents.list(clientId);
      setDocs(res.data.results || res.data || []);
    } catch {
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!file || !docType) return;
    setUploading(true);
    setError('');
    try {
      await medicalTourismAPI.documents.create({ client: clientId, document_type: docType, file });
      setFile(null);
      setShowForm(false);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title">
        <span>Other files (mobile app / staff)</span>
        <button type="button" className="cv-inline-btn" onClick={() => setShowForm((v) => !v)}>
          <FiUpload size={12} /> {showForm ? 'Cancel' : 'Upload'}
        </button>
      </div>

      {showForm && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className="filter-select">
            {DOCUMENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} style={{ fontSize: 12 }} />
          <button type="button" className="btn-primary" disabled={!file || uploading} onClick={handleUpload} style={{ padding: '7px 14px', fontSize: 12, minWidth: 'auto' }}>
            {uploading ? 'Uploading…' : 'Save'}
          </button>
        </div>
      )}

      {error && <div className="error-message" style={{ fontSize: 12, padding: '6px 10px' }}>{error}</div>}

      {loading ? (
        <div className="cv-subsection-empty">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="cv-subsection-empty">No documents uploaded yet.</div>
      ) : (
        docs.map((d) => (
          <div key={d.id} className="cv-doc-row">
            <FiFile size={13} style={{ color: '#011ff3', flexShrink: 0 }} />
            <span style={{ fontWeight: 600 }}>{d.document_type_display || d.document_type}</span>
            <span style={{ color: '#999', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.file_name || d.file}</span>
            <span style={{ color: '#999' }}>{fmtDate(d.uploaded_at || d.created_at)}</span>
            {d.file && <a href={d.file} target="_blank" rel="noreferrer" style={{ color: '#011ff3' }}>Open</a>}
          </div>
        ))
      )}
    </div>
  );
};

// ── Progress log sub-section (status-history notes) ──
const ClientProgressLog = ({ clientId, currentStatus }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [logStatus, setLogStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await medicalTourismAPI.progressLogs.list(clientId);
      setLogs(res.data.results || res.data || []);
    } catch {
      setError('Failed to load progress notes.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!note.trim()) return;
    setSaving(true);
    setError('');
    try {
      await medicalTourismAPI.progressLogs.create({
        client: clientId,
        ...(logStatus ? { status: logStatus } : {}),
        note: note.trim(),
      });
      setNote('');
      setLogStatus('');
      load();
    } catch {
      setError('Failed to add note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>Progress / Status Notes</span></div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <select value={logStatus} onChange={(e) => setLogStatus(e.target.value)} className="filter-select">
          <option value="">Note only (no status change)</option>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}{v === currentStatus ? ' (current)' : ''}</option>)}
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…"
          style={{ flex: 1, minWidth: 160, padding: '7px 10px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 12 }} />
        <button type="button" className="btn-primary" disabled={!note.trim() || saving} onClick={handleAdd} style={{ padding: '7px 14px', fontSize: 12, minWidth: 'auto' }}>
          {saving ? 'Saving…' : 'Add'}
        </button>
      </div>

      {error && <div className="error-message" style={{ fontSize: 12, padding: '6px 10px' }}>{error}</div>}

      {loading ? (
        <div className="cv-subsection-empty">Loading…</div>
      ) : logs.length === 0 ? (
        <div className="cv-subsection-empty">No progress notes yet.</div>
      ) : (
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {logs.map((l) => (
            <div key={l.id} className="cv-log-row" style={{ display: 'block' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <FiClock size={11} style={{ color: '#999' }} />
                <span style={{ color: '#999' }}>{fmtDateTime(l.created_at)}</span>
                {l.status && <span style={{ color: statusColor(l.status), fontWeight: 600 }}>→ {l.status_display || statusLabel(l.status)}</span>}
              </div>
              <div>{l.note}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Create / Edit modal ──
const ClientModal = ({ initial, agents, onSave, onCancel, onRefresh, loading }) => {
  const isEdit = !!initial?.id;
  const { canSetPaymentStatus } = useAuth();
  const canSetPayment = canSetPaymentStatus();
  const [form, setForm] = useState({
    first_name: initial?.first_name || '',
    last_name: initial?.last_name || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    status: initial?.status || 'NEW',
    assigned_agent: initial?.assigned_agent || '',
    procedure_type: initial?.procedure_type || '',
    procedure_notes: initial?.procedure_notes || '',
    preferred_destination_country: initial?.preferred_destination_country || '',
    preferred_hospital: initial?.preferred_hospital || '',
    payment_status: initial?.payment_status || 'NOT_PAID',
    total_service_fee: initial?.total_service_fee ?? '',
    date_of_birth: initial?.date_of_birth || '',
    gender: initial?.gender || '',
    passport_number: initial?.passport_number || '',
    country: initial?.country || '',
    city: initial?.city || '',
    full_address: initial?.full_address || '',
    notes: initial?.notes || '',
  });
  const [collaboratorIds, setCollaboratorIds] = useState(
    (initial?.assigned_agents_detail || []).map((a) => String(a.id))
  );
  const toggleCollaborator = (id) => setCollaboratorIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );

  const [client, setClient] = useState(initial); // kept fresh after ban/unban/login-setup
  const [banPrompt, setBanPrompt] = useState(null); // null | 'block' | 'unblock'
  const [banReason, setBanReason] = useState('');
  const [banBusy, setBanBusy] = useState(false);
  const [issuingLogin, setIssuingLogin] = useState(false);
  const [loginSetup, setLoginSetup] = useState(null);
  const [copyOk, setCopyOk] = useState(false);
  const [actionError, setActionError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSave = form.first_name.trim() && form.last_name.trim() && form.email.trim() && !loading;

  const confirmBan = async () => {
    if (!client?.id) return;
    if (banPrompt === 'block' && !banReason.trim()) {
      setActionError('Please enter a reason for banning.');
      return;
    }
    setBanBusy(true);
    setActionError('');
    try {
      if (banPrompt === 'block') {
        const res = await medicalTourismAPI.ban(client.id, banReason.trim());
        setClient(res.data);
        if (onRefresh) onRefresh(res.data);
      } else {
        const res = await medicalTourismAPI.unban(client.id);
        setClient(res.data);
        if (onRefresh) onRefresh(res.data);
      }
      setBanPrompt(null);
      setBanReason('');
    } catch (err) {
      setActionError(err?.response?.data?.error || err?.response?.data?.detail || 'Action failed. Please try again.');
    } finally {
      setBanBusy(false);
    }
  };

  const handleIssueLoginSetup = async () => {
    if (!client?.id) return;
    setIssuingLogin(true);
    setActionError('');
    setCopyOk(false);
    try {
      const res = await medicalTourismAPI.issueLoginSetup(client.id);
      setLoginSetup(res.data);
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Could not generate a login link.');
    } finally {
      setIssuingLogin(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div>
            <h2>{isEdit ? 'Edit Medical Tourism Client' : 'New Medical Tourism Client'}</h2>
            {isEdit && <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>{client?.client_id}</div>}
          </div>
          <button className="modal-close" onClick={onCancel}><FiX /></button>
        </div>

        <div className="modal-form">
          {isEdit && client?.is_banned && (
            <div className="error-message">
              Banned{client.banned_at ? ` on ${fmtDateTime(client.banned_at)}` : ''}{client.ban_reason ? `: ${client.ban_reason}` : ''}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Assigned Agent</label>
              <select value={form.assigned_agent} onChange={(e) => set('assigned_agent', e.target.value)}>
                <option value="">— none —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.displayName || a.username}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Also assign (co-workers who can also see this record)</label>
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 8, maxHeight: 130, overflowY: 'auto' }}>
              {agents.filter((a) => String(a.id) !== String(form.assigned_agent)).map((a) => (
                <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" style={{ width: 'auto' }}
                    checked={collaboratorIds.includes(String(a.id))}
                    onChange={() => toggleCollaborator(String(a.id))} />
                  {a.displayName || a.username}
                </label>
              ))}
              {agents.length === 0 && <div style={{ color: '#999', fontSize: 12 }}>No agents/employees to add.</div>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Procedure Type</label>
              <select value={form.procedure_type} onChange={(e) => set('procedure_type', e.target.value)}>
                <option value="">— select —</option>
                {PROCEDURE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Preferred Destination Country</label>
              <input value={form.preferred_destination_country} onChange={(e) => set('preferred_destination_country', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>Procedure Notes</label>
            <textarea value={form.procedure_notes} onChange={(e) => set('procedure_notes', e.target.value)} rows={2} />
          </div>

          <div className="form-group">
            <label>Preferred Hospital</label>
            <input value={form.preferred_hospital} onChange={(e) => set('preferred_hospital', e.target.value)} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Payment Status</label>
              <select value={form.payment_status} disabled={!canSetPayment} onChange={(e) => set('payment_status', e.target.value)}>
                {PAYMENT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {!canSetPayment && <small style={{ color: '#64748b' }}>Only the CFO, Chairman, CEO or COO changes this.</small>}
            </div>
            <div className="form-group">
              <label>Total Service Fee (USD)</label>
              <input type="number" min="0" step="0.01" value={form.total_service_fee} onChange={(e) => set('total_service_fee', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth</label>
              <input type="date" value={form.date_of_birth || ''} onChange={(e) => set('date_of_birth', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                <option value="">— select —</option>
                {GENDER_CHOICES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Passport Number</label>
            <input value={form.passport_number} onChange={(e) => set('passport_number', e.target.value)} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              <input value={form.country} onChange={(e) => set('country', e.target.value)} />
            </div>
            <div className="form-group">
              <label>City</label>
              <input value={form.city} onChange={(e) => set('city', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>Full Address</label>
            <textarea value={form.full_address} onChange={(e) => set('full_address', e.target.value)} rows={2} />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} />
          </div>

          {actionError && <div className="error-message">{actionError}</div>}
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!canSave}
            onClick={() => onSave({
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              email: form.email.trim(),
              phone: form.phone || '',
              status: form.status,
              assigned_agent: form.assigned_agent ? parseInt(form.assigned_agent, 10) : null,
              assigned_agent_ids: collaboratorIds.map((id) => parseInt(id, 10)),
              procedure_type: form.procedure_type || null,
              procedure_notes: form.procedure_notes || '',
              preferred_destination_country: form.preferred_destination_country || '',
              preferred_hospital: form.preferred_hospital || '',
              payment_status: form.payment_status,
              total_service_fee: form.total_service_fee === '' ? null : form.total_service_fee,
              date_of_birth: form.date_of_birth || null,
              gender: form.gender || null,
              passport_number: form.passport_number || '',
              country: form.country || '',
              city: form.city || '',
              full_address: form.full_address || '',
              notes: form.notes || '',
            })}
          >
            {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Client'}
          </button>
        </div>

        {/* ── Ban / Unban + Login Setup — only for existing clients ── */}
        {isEdit && (
          <div className="cv-subsection" style={{ margin: '0 20px 20px' }}>
            <div className="cv-subsection-title"><span>Client App Access</span></div>
            <div className="cv-access-row">
              {client?.is_banned ? (
                <button type="button" className="cv-inline-btn" onClick={() => { setBanPrompt('unblock'); setBanReason(''); setActionError(''); }}
                  style={{ background: '#dcfce7', color: '#166534', border: 'none' }}>
                  <FiUnlock size={13} /> Unban Client
                </button>
              ) : (
                <button type="button" className="cv-inline-btn" onClick={() => { setBanPrompt('block'); setBanReason(''); setActionError(''); }}
                  style={{ background: '#fee2e2', color: '#991b1b', border: 'none' }}>
                  <FiLock size={13} /> Ban Client
                </button>
              )}
              <button type="button" className="cv-inline-btn" disabled={issuingLogin} onClick={handleIssueLoginSetup}>
                <FiKey size={13} /> {issuingLogin ? 'Generating…' : 'Issue Login Setup Link'}
              </button>
            </div>

            {loginSetup && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>
                  Send this to the client over WhatsApp/email. Expires in {loginSetup.expires_hours}h. One-time use.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input readOnly value={loginSetup.link || loginSetup.token} onFocus={(e) => e.target.select()}
                    style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 12 }} />
                  <button type="button" className="cv-inline-btn" onClick={() => {
                    navigator.clipboard?.writeText(loginSetup.link || loginSetup.token);
                    setCopyOk(true);
                    setTimeout(() => setCopyOk(false), 1500);
                  }}>
                    <FiCopy size={12} /> {copyOk ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                {!loginSetup.link && (
                  <p style={{ color: '#f5a623', fontSize: 11, margin: '6px 0 0' }}>
                    No setup-page URL configured (System Settings → student_password_setup_url). The client pastes this raw token in the app's "Set / reset password" screen.
                  </p>
                )}
              </div>
            )}
            <p style={{ color: '#999', fontSize: 11, margin: '8px 0 0' }}>The client sets their own password from this link. Audit-logged.</p>
          </div>
        )}

        {/* ── Documents + Progress log — only for existing clients ── */}
        {isEdit && client?.id && <LessonsPanel kind="medical" clientId={client.id} />}
        {isEdit && client?.id && (
          <div style={{ padding: '0 20px 20px' }}>
            <ClientJourneyPanel kind="medical" clientId={client.id} />
            <ClientStepsPanel kind="MEDICAL" clientId={client.id} leadSource={client.lead_source} leadPaid={client.lead_paid} />
            <ClientDocuments clientId={client.id} />
            <ClientProgressLog clientId={client.id} currentStatus={form.status} />
          </div>
        )}
      </div>

      {/* Ban / Unban confirm prompt */}
      {banPrompt && (
        <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={() => !banBusy && setBanPrompt(null)}>
          <div className="modal-content" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: 16 }}>{banPrompt === 'block' ? 'Ban Client' : 'Unban Client'}</h2>
            </div>
            <div className="modal-form">
              <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
                {banPrompt === 'block'
                  ? `Block ${client?.full_name || 'this client'} from accessing the app?`
                  : `Restore app access for ${client?.full_name || 'this client'}?`}
              </p>
              {banPrompt === 'block' && (
                <div className="form-group">
                  <textarea value={banReason} onChange={(e) => setBanReason(e.target.value)} placeholder="Reason for banning (required)…" rows={3} />
                </div>
              )}
              {actionError && <div className="error-message">{actionError}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setBanPrompt(null)} disabled={banBusy}>Cancel</button>
              <button
                className="btn-primary"
                onClick={confirmBan}
                disabled={banBusy}
                style={banPrompt === 'block' ? { background: '#dc2626', borderColor: '#dc2626' } : { background: '#16a34a', borderColor: '#16a34a' }}
              >
                {banBusy ? 'Please wait…' : banPrompt === 'block' ? 'Ban Client' : 'Unban Client'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MedicalTourism = () => {
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [procedureFilter, setProcedureFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | {} (new) | client (edit)
  const [actionLoading, setActionLoading] = useState(false);

  // Debounce search box → search query param
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ordering: '-created_at' };
      if (statusFilter) params.status = statusFilter;
      if (procedureFilter) params.procedure_type = procedureFilter;
      if (search) params.search = search;
      const res = await medicalTourismAPI.list(params);
      setClients(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load medical tourism clients.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, procedureFilter, search]);

  const loadAgents = useCallback(async () => {
    try {
      const [agentsRes, employeesRes] = await Promise.all([
        usersAPI.agents(),
        usersAPI.permanentEmployees().catch(() => ({ data: [] })),
      ]);
      const agentsData = agentsRes.data || [];
      const employeesData = employeesRes.data || [];
      const combined = [
        ...agentsData.map((a) => ({ ...a, displayName: `${a.username} (Agent)` })),
        ...employeesData.map((e) => ({ ...e, displayName: `${e.username} (Employee)` })),
      ];
      setAgents(combined);
    } catch {
      setAgents([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAgents(); }, [loadAgents]);

  const handleSave = async (data) => {
    setActionLoading(true);
    try {
      if (modal?.id) await medicalTourismAPI.update(modal.id, data);
      else await medicalTourismAPI.create(data);
      setModal(null);
      load();
    } catch (e) {
      setError(JSON.stringify(e?.response?.data) || 'Failed to save client.');
    } finally {
      setActionLoading(false);
    }
  };

  const openEdit = async (row) => {
    // Use the full row for now; ClientModal keeps itself in sync via
    // ban/unban/login-setup responses, and edits still PATCH by id.
    setModal(row);
  };
  useOpenParam('client', (id) => medicalTourismAPI.get(id).then((r) => setModal(r.data)).catch(() => {}));

  return (
    <div className="students-page">
      <div className="page-header">
        <div className="header-actions-left">
          <button className="btn-primary" onClick={() => setModal({})}>
            <FiPlus /> New Client
          </button>
        </div>
        <div className="header-actions-right">
          <div className="search-box">
            <FiSearch />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, email, phone, client ID…" />
          </div>
          <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="filter-select" value={procedureFilter} onChange={(e) => setProcedureFilter(e.target.value)}>
            <option value="">All Procedures</option>
            {PROCEDURE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className="btn-icon" onClick={load} title="Refresh">
            <FiRefreshCw className={loading ? 'cv-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiAlertCircle size={16} />
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={14} /></button>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="empty-state">No medical tourism clients found.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Client ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Procedure</th>
                <th>Agent</th>
                <th>Payment</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} onClick={() => openEdit(c)} style={{ cursor: 'pointer' }}>
                  <td><span className="cv-id">{c.client_id}</span></td>
                  <td>
                    <strong>{c.full_name || `${c.first_name} ${c.last_name}`}</strong>
                    <div style={{ color: '#999', fontSize: 12 }}>{c.email}</div>
                  </td>
                  <td>
                    <span className="status-pill" style={{ color: statusColor(c.status) }}>
                      {c.status_display || statusLabel(c.status)}
                    </span>
                    {c.is_banned && <span className="banned-pill">BANNED</span>}
                  </td>
                  <td>{c.procedure_type_display || '—'}</td>
                  <td>{c.assigned_agent_name || '—'}</td>
                  <td><span style={{ color: paymentColor(c.payment_status), fontWeight: 600, fontSize: 12 }}>{c.payment_status_display}</span></td>
                  <td>{fmtDate(c.created_at)}</td>
                  <td>
                    <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openEdit(c); }} title="Edit">
                      <FiEdit2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ClientModal
          agents={agents}
          initial={modal.id ? modal : null}
          onSave={handleSave}
          onCancel={() => setModal(null)}
          onRefresh={(updated) => {
            setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
          }}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default MedicalTourism;
